// /login/password-change — first-login forced rotation of the temp password.
// Posts to /v1/auth/password/change with { flow_session, new_password }.

import { ErrorBanner, FormField, LoginShell } from '@/components/LoginShell';
import { ApiError, authFetch } from '@/lib/api';
import { useOnceMountGuard } from '@/lib/auth-guards';
import { useAuth } from '@/state/auth';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';

export const Route = createFileRoute('/login/password-change')({
  component: PasswordChangePage,
});

function strengthHint(pw: string): {
  score: number;
  label: string;
  tone: 'rd' | 'amb' | 'grn' | 'dim2';
} {
  if (pw.length < 14) return { score: 0, label: 'TOO SHORT (≥14 CHARS)', tone: 'rd' };
  let score = 1;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  if (score >= 4) return { score, label: 'STRONG', tone: 'grn' };
  if (score >= 3) return { score, label: 'ACCEPTABLE', tone: 'amb' };
  return { score, label: 'WEAK', tone: 'rd' };
}

function PasswordChangePage() {
  const flowSession = useAuth((s) => s.flowSession);
  const setFlow = useAuth((s) => s.setFlow);
  const navigate = useNavigate();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useOnceMountGuard(() => useAuth.getState().flowSession != null, { to: '/login' });

  const strength = strengthHint(pw);
  const matches = pw && pw === pw2;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!flowSession) return;
    if (strength.score < 3) {
      setErr('weak_password');
      return;
    }
    if (!matches) {
      setErr('invalid_request');
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const data = await authFetch<{ step: string; flow_session: string }>(
        '/v1/auth/password/change',
        { method: 'POST', body: { flow_session: flowSession, new_password: pw } },
      );
      setFlow(data.flow_session ?? null, data.step as never);
      switch (data.step) {
        case 'mfa_enroll_required':
          void navigate({ to: '/login/mfa-enroll' });
          break;
        case 'mfa_required':
          void navigate({ to: '/login/mfa-verify' });
          break;
        default:
          setErr('internal_error');
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.code : 'internal_error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <LoginShell
      step="STEP 2 / 4 · CHANGE PASSWORD"
      title="Set a new password"
      sub="Your temporary password must be replaced before you can access the terminal. 14+ characters; mix of upper, lower, number, and symbol."
    >
      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <ErrorBanner code={err} />
        <FormField label="NEW PASSWORD" mono>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            autoComplete="new-password"
            required
            style={{ flex: 1, fontFamily: 'var(--mono)' }}
          />
        </FormField>
        <FormField label="CONFIRM" mono>
          <input
            type="password"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
            autoComplete="new-password"
            required
            style={{ flex: 1, fontFamily: 'var(--mono)' }}
          />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <span className={`meta mono ${strength.tone}`}>{pw ? strength.label : ''}</span>
          {pw2 && (
            <span className={`meta mono ${matches ? 'grn' : 'rd'}`}>
              {matches ? 'MATCH' : 'NO MATCH'}
            </span>
          )}
        </div>
        <button
          type="submit"
          className="btn pri"
          disabled={busy || strength.score < 3 || !matches}
          style={{ height: 32, justifyContent: 'center', marginTop: 4 }}
        >
          {busy ? 'UPDATING…' : 'CONTINUE'}
        </button>
      </form>
    </LoginShell>
  );
}
